/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IHeaderViewProps } from '@msdyn365-commerce-modules/header/src/modules/header/./header';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

const headerView: React.FC<IHeaderViewProps> = props => {
    const isAuthenticated = props.context.request.user.isAuthenticated;
    const headerConfig =
        (props as {
            config?: {
                disableLogin?: boolean;
                hideProtectedContentForAnonymousUsers?: boolean;
            };
        }).config || {};
    const shouldDisableLogin = headerConfig.disableLogin;
    const shouldHideProtectedContentForAnonymousUsers = headerConfig.hideProtectedContentForAnonymousUsers;
    const shouldShowProtectedContent = !shouldDisableLogin && (!shouldHideProtectedContentForAnonymousUsers || isAuthenticated);

    const {
        HeaderTag,
        HeaderContainer,
        HeaderTopBarContainer,
        MobileMenuContainer,
        MobileMenuBodyContainer,
        MobileMenuLinksContainer,
        Divider
    } = props;
    return (
        <Module {...HeaderTag}>
            <Node {...HeaderContainer}>
                <Node {...HeaderTopBarContainer}>
                    {/* Logo Image */}
                    {props.logo}
                    {/* Navigation Menu */}
                    {_renderReactFragment(props.menuBar)}
                    {/* Searchbar */}
                    {_renderReactFragment(props.search)}
                    <Node {...Divider} />
                    {props.preferredStore}
                    {/* Language Select */}
                    {_renderReactFragment(props.siteOptions)}
                    {/* Sign In */}
                    {shouldShowProtectedContent ? _renderDesktopAccountBlock(props) : null}
                    {/* Cart Icon */}
                    {shouldHideProtectedContentForAnonymousUsers ? null : props.cartIcon}
                </Node>
                {/* This creates hidden modal menus */}
                <Node {...MobileMenuContainer}>
                    <Node {...MobileMenuBodyContainer}>
                        {props.MobileMenuHeader}
                        <Node {...MobileMenuLinksContainer}>
                            {shouldShowProtectedContent && props.accountLinks ? props.accountLinks.map(link => link) : false}
                            {props.siteOptions}
                            {shouldShowProtectedContent ? props.signInLink : null}
                            {shouldShowProtectedContent ? props.signOutLink : null}
                        </Node>
                    </Node>
                    {_renderReactFragment(props.menuBar)}
                </Node>
            </Node>
        </Module>
    );
};

function _renderDesktopAccountBlock(props: IHeaderViewProps): JSX.Element | null {
    const {
        AccountInfoDropdownParentContainer,
        AccountInfoDropdownPopoverConentContainer,
        accountInfoDropdownButton,
        signOutLink,
        signInLink,
        accountLinks
    } = props;

    if (AccountInfoDropdownParentContainer) {
        if (AccountInfoDropdownPopoverConentContainer) {
            return (
                <Node {...AccountInfoDropdownParentContainer}>
                    {accountInfoDropdownButton}
                    <Node {...AccountInfoDropdownPopoverConentContainer}>
                        {accountLinks ? accountLinks.map(link => link) : false}
                        {signOutLink}
                    </Node>
                </Node>
            );
        } else if (signInLink) {
            return <Node {...AccountInfoDropdownParentContainer}>{signInLink}</Node>;
        }
    }
    props.context.telemetry.error('Header content is empty, module wont render.');
    return null;
}

function _renderReactFragment(items: React.ReactNode[]): JSX.Element | null {
    return (
        <>
            {items && items.length > 0
                ? items.map((slot: React.ReactNode, index: number) => {
                      return <React.Fragment key={index}>{slot}</React.Fragment>;
                  })
                : null}
        </>
    );
}

export default headerView;
